import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Tag } from 'antd';
import DataTable from '@/components/data-table';
import useRowOpen from '@/components/data-table/useRowOpen';
import InvoiceStatus from '@/components/invoice/InvoiceStatus';
import { OPERATION, ROUTES } from '@/constants';
import { API } from '@/services/api.service';
import { ReasonText } from '../../_components/UnbookedInvoices';
import { date, money } from '../../_components/format';

/** The incoming invoice's own view. */
const invoiceUrl = (row) => `${ROUTES.admin}/${ROUTES.incomingInvoices}/${OPERATION.view}/${row.invoiceId}`;

/**
 * All incoming invoices of the supplier on the platform, newest first, paged
 * on the server (`iOffset` / `iLimit`). Under the ledger switch a neutral
 * "in progress" tag marks the ones not in the books yet; our roles also get
 * the days since receipt and the reason (the server sends them to nobody else).
 */
export default function SupplierInvoices({ company, pib, queryParams, setQueryParams }) {
  const { t } = useTranslation(['suppliers', 'ledgerCards']);
  const page = { offset: String(queryParams.iOffset || '1'), limit: String(queryParams.iLimit || '10') };
  const { data, isLoading, error } = useQuery({
    queryKey: ['suppliers', company, 'invoices', pib, page],
    queryFn: () => API.suppliers.getInvoices(company, pib, page),
    enabled: Boolean(company && pib),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const internal = (data?.items || []).some((i) => 'reason' in i);
  // A row with an invoice on the platform opens it, whole row wide; Ctrl/Cmd + click opens a new tab.
  const openInvoice = useRowOpen(invoiceUrl);
  const onRow = (row) => (row.invoiceId ? openInvoice(row) : {});

  const columns = [
    {
      title: t('invoices.number'),
      dataIndex: 'documentNumber',
      key: 'documentNumber',
      width: 180,
      ellipsis: true,
      render: (v, row) =>
        row.invoiceId ? (
          <Link to={invoiceUrl(row)}>{v}</Link>
        ) : (
          v
        ),
    },
    { title: t('invoices.date'), dataIndex: 'documentDate', key: 'documentDate', width: 110, render: date },
    { title: t('invoices.due'), dataIndex: 'dueDate', key: 'dueDate', width: 110, render: date },
    {
      title: t('invoices.gross'),
      key: 'gross',
      width: 150,
      align: 'right',
      render: (_, row) => money(row.debit ? -row.debit : row.credit),
    },
    { title: t('invoices.currency'), dataIndex: 'currency', key: 'currency', width: 80 },
    {
      title: t('invoices.status'),
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (s) =>
        s?.key ? <InvoiceStatus status={{ ...s, label: t(`unbooked.statuses.${s.key}`, { ns: 'ledgerCards', defaultValue: s.key }) }} /> : null,
    },
    {
      title: t('invoices.payment'),
      dataIndex: 'payment',
      key: 'payment',
      width: 160,
      render: (p) => (p ? `${t(`invoices.paymentStatus.${p.status}`)} · ${money(p.paidAmount)}` : ''),
    },
    {
      title: t('invoices.project'),
      dataIndex: 'project',
      key: 'project',
      width: 180,
      ellipsis: true,
      render: (p) => (p ? [p.code, p.name].filter(Boolean).join(' ') : ''),
    },
    data?.ledger && {
      title: t('invoices.inProgress'),
      dataIndex: 'inProgress',
      key: 'inProgress',
      width: 140,
      render: (v) => (v ? <Tag color="blue">{t('invoices.inProgressTag')}</Tag> : null),
    },
    internal && { title: t('unbooked.days', { ns: 'ledgerCards' }), dataIndex: 'daysSinceReceipt', key: 'days', width: 110, align: 'right' },
    internal && {
      title: t('unbooked.reason', { ns: 'ledgerCards' }),
      dataIndex: 'reason',
      key: 'reason',
      width: 260,
      ellipsis: { showTitle: false },
      render: (r) => (r ? <ReasonText reason={r} /> : null),
    },
  ].filter(Boolean);

  return (
    <DataTable
      title={`${t('profile.tabs.invoices')}${data ? ` · ${data.count}` : ''}`}
      data={data?.items || []}
      total={data?.count || 0}
      status={isLoading ? 'pending' : error ? 'error' : 'success'}
      columns={columns}
      offset={page.offset}
      limit={page.limit}
      syncUrl={false}
      onRow={onRow}
      onChangePagination={(key, value) => setQueryParams(key === 'limit' ? { iLimit: value, iOffset: 1 } : { iOffset: value })}
      tableLayout="fixed"
      scroll={{ x: columns.reduce((s, c) => s + (c.width || 0), 0) }}
      size="small"
    />
  );
}
